import type { FC, ReactNode } from 'react'
import type { Article, ArticleContent } from '@/types'
import type { TocEntry } from '@/components/article/article-body'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ChevronDown, History, ListTree } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatJalali, toFaDigits } from '@/lib/format'
import { ConfluenceHint } from '@/components/generals/confluence-hint'
import { Badge } from '@/components/ds/badge'
import { cardStyles } from '@/components/ds/card'
import { ConfidentialityBadge, LabelChip, StatusBadge } from '@/components/knowledge/badges'
import { PERSON_BY_SLUG } from '@/constants/people'

import {
    CATEGORY_BY_SLUG,
    TYPE_BY_KEY,
    UNIT_BY_KEY,
    subcategoryTitle
} from '@/constants/taxonomy'

interface TableOfContentsProps {
    entries: TocEntry[]
    variant?: 'card' | 'compact'
}

export const TableOfContents: FC<TableOfContentsProps> = ({ entries, variant = 'card' }): ReactNode => {
    const [active, setActive] = useState(entries[0]?.id ?? '')
    const idsKey = entries.map(entry => entry.id).join('|')

    useEffect(() => {
        const ids = idsKey.split('|')

        const handleScroll = () => {
            let current = ids[0]

            for (const id of ids) {
                const element = document.getElementById(id)

                if (element && element.getBoundingClientRect().top <= 140) current = id
            }

            setActive(current)
        }

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [idsKey])

    const list = (
        <ol className='flex flex-col gap-0.5 border-s border-border'>
            {entries.map((entry, index) => (
                <li key={entry.id}>
                    <a
                        href={`#${entry.id}`}
                        aria-current={active === entry.id ? 'location' : undefined}
                        className={cn(
                            '-ms-px flex gap-2 border-s-2 py-1.5 ps-3 text-small transition-colors hover:text-primary-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                            active === entry.id ? 'border-primary font-semibold text-primary-deep' : 'border-transparent text-muted-foreground'
                        )}
                    >
                        <span className='tabular-nums text-subtle'>{toFaDigits(index + 1)}</span>
                        {entry.title}
                    </a>
                </li>
            ))}
        </ol>
    )

    if (variant === 'compact') {
        return (
            <details className='group rounded-xl border border-border bg-card shadow-card'>
                <summary className='flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-bold text-navy [&::-webkit-details-marker]:hidden'>
                    <span className='flex items-center gap-2'>
                        <ListTree className='size-4 text-primary' aria-hidden='true' />
                        فهرست مطالب
                    </span>

                    <ChevronDown className='size-4 text-muted-foreground transition-transform group-open:rotate-180' aria-hidden='true' />
                </summary>

                <div className='px-4 pb-4'>{list}</div>
            </details>
        )
    }

    return (
        <nav aria-labelledby='toc-title' className={cardStyles(false, 'relative p-5')}>
            <ConfluenceHint id='article-toc' />

            <p id='toc-title' className='mb-3 flex items-center gap-2 text-sm font-bold text-navy'>
                <ListTree className='size-4 text-primary' aria-hidden='true' />
                فهرست مطالب
            </p>

            {list}
        </nav>
    )
}

interface ArticlePropertiesProps {
    article: Article
    version: number
}

export const ArticleProperties: FC<ArticlePropertiesProps> = ({ article, version }): ReactNode => {
    const owner = PERSON_BY_SLUG[article.owner]

    const rows: { label: string, value: ReactNode }[] = [
        { label: 'نوع دانش', value: TYPE_BY_KEY[article.type].title },
        { label: 'دسته‌بندی', value: <Link href={`/categories/${article.category}`} className='text-primary-strong hover:text-primary-deep'>{CATEGORY_BY_SLUG[article.category]?.title}</Link> },
        { label: 'زیرمجموعه', value: <Link href={`/categories/${article.category}?sub=${article.subcategory}`} className='text-primary-strong hover:text-primary-deep'>{subcategoryTitle(article.category, article.subcategory)}</Link> },
        { label: 'واحد سازمانی', value: UNIT_BY_KEY[article.unit]?.title },
        { label: 'مالک دانش', value: owner ? <Link href={`/people/${owner.slug}`} className='text-primary-strong hover:text-primary-deep'>{owner.name}</Link> : '-' },
        { label: 'سطح محرمانگی', value: <ConfidentialityBadge level={article.confidentiality} /> },
        { label: 'وضعیت', value: <StatusBadge status={article.status} /> },
        { label: 'نسخه', value: <Badge tone='outline'>نسخه {toFaDigits(version)}</Badge> }
    ]

    return (
        <section aria-labelledby='properties-title' className={cardStyles(false, 'relative p-5')}>
            <ConfluenceHint id='article-meta' />

            <h2 id='properties-title' className='text-sm font-bold text-navy'>اطلاعات دانش</h2>

            <dl className='mt-3 flex flex-col divide-y divide-border'>
                {rows.map(row => (
                    <div key={row.label} className='flex items-center justify-between gap-3 py-2.5'>
                        <dt className='shrink-0 text-small text-muted-foreground'>{row.label}</dt>
                        <dd className='min-w-0 text-end text-small font-medium text-foreground'>{row.value}</dd>
                    </div>
                ))}
            </dl>

            <div className='mt-3 flex flex-col gap-2 border-t border-border pt-3'>
                <p className='text-small text-muted-foreground'>برچسب‌ها</p>

                <div className='flex flex-wrap gap-1.5'>
                    {article.labels.map(label => (
                        <LabelChip key={label} label={label} />
                    ))}
                </div>
            </div>
        </section>
    )
}

interface ArticleHistoryProps {
    history: ArticleContent['history']
}

export const ArticleHistory: FC<ArticleHistoryProps> = ({ history }): ReactNode => {
    const [expanded, setExpanded] = useState(false)
    const items = expanded ? history : history.slice(0, 3)

    return (
        <section aria-labelledby='history-title' className={cardStyles(false, 'relative p-5')}>
            <ConfluenceHint id='article-history' />

            <h2 id='history-title' className='flex items-center gap-2 text-sm font-bold text-navy'>
                <History className='size-4 text-primary' aria-hidden='true' />
                تاریخچه نسخه‌ها
            </h2>

            <ol className='mt-4 flex flex-col gap-4'>
                {items.map((item, index) => {
                    const author = PERSON_BY_SLUG[item.author]

                    return (
                        <li key={item.version} className='relative flex gap-3'>
                            {index < items.length - 1 && <span className='absolute -bottom-4 start-[5px] top-4 w-px bg-border' aria-hidden='true' />}

                            <span className={cn('relative mt-1.5 size-[11px] shrink-0 rounded-full border-2', index === 0 ? 'border-primary bg-primary' : 'border-input bg-card')} aria-hidden='true' />

                            <div className='flex min-w-0 flex-col gap-0.5'>
                                <p className='flex flex-wrap items-center gap-2 text-small font-semibold text-navy'>
                                    نسخه {toFaDigits(item.version)}
                                    {index === 0 && <Badge tone='primary'>نسخه فعلی</Badge>}
                                </p>

                                <p className='text-small text-foreground'>{item.note}</p>

                                <p className='text-caption text-muted-foreground'>
                                    {formatJalali(item.date)}
                                    {author && <> · {author.name}</>}
                                </p>
                            </div>
                        </li>
                    )
                })}
            </ol>

            {history.length > 3 && (
                <button
                    type='button'
                    onClick={() => setExpanded(value => !value)}
                    aria-expanded={expanded}
                    className='mt-4 cursor-pointer text-small font-semibold text-primary-strong transition-colors hover:text-primary-deep'
                >
                    {expanded ? 'نمایش کمتر' : `نمایش همه ${toFaDigits(history.length)} نسخه`}
                </button>
            )}
        </section>
    )
}
